import React, { useState, useEffect, useRef } from 'react';
import { AnimatePresence } from 'framer-motion';
import { collection, getDocs, query, orderBy, addDoc, serverTimestamp } from 'firebase/firestore';
import { testFirebaseConnection, initializeDefaultContent, getSystemSettings, updateSystemSettings, db, logChatEvent, logUIEvent, logSystemEvent, logErrorEvent, logPerformanceEvent } from '../../services/firebase';
import { isJobApplicationMessage } from '../../services/dataCollection';
import ollamaService from '../../services/ollamaService';
import aiService from '../../services/aiService';
import toast from 'react-hot-toast';
import { onAuthStateChange, getCurrentUserData, createUserDocument, forceCreateCurrentUserDocument, ensureUserDocumentExists } from '../../services/authService';

import Header from '../Layout/Header';
import ChatHeader from './ChatHeader';
import ActionButtons from './ActionButtons';
import MessageList from './MessageList';
import ChatInput from './ChatInput';
import Footer from '../Layout/Footer';
import VoiceModal from './VoiceModal';
import UserAuth from '../Auth/UserAuth';
import UserArea from '../User/UserArea';

const ChatApp = () => {
  
  // Core states
  const [messages, setMessages] = useState([]);
  const [isTyping, setIsTyping] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [contentDatabase, setContentDatabase] = useState([]);
  const [systemSettings, setSystemSettings] = useState(null);
  const [ollamaAvailable, setOllamaAvailable] = useState(false);
  const [firebaseConnected, setFirebaseConnected] = useState(false);

  const [currentProvider, setCurrentProvider] = useState(null);
  
  // Conversation tracking
  const [conversationState, setConversationState] = useState({
    messageCount: 0,
    leadGenerationTriggered: false,
    jobApplicationTriggered: false,
    collectingLeadInfo: false,
    collectingJobInfo: false,
    leadData: {},
    jobData: {},
    currentStep: null
  });
  
  // UI states
  const [darkMode, setDarkMode] = useState(false);
  const [showVoiceModal, setShowVoiceModal] = useState(false);
  const [voiceEnabled, setVoiceEnabled] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const messagesEndRef = useRef(null);
  const recognitionRef = useRef(null);
  
  // User authentication state
  const [user, setUser] = useState(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [userData, setUserData] = useState(null);

  // Lead collection steps
  const leadSteps = [
    { key: 'name', question: 'Thank you. What\'s your company or organization?', validation: (val) => val.length > 1 },
    { key: 'email', question: 'What\'s the best email address to reach you?', validation: (val) => /\S+@\S+\.\S+/.test(val) },
    { key: 'company', question: 'What\'s your role or position within the organization?', validation: (val) => val.length > 1 },
    { key: 'designation', question: 'What industry sector does your organization operate in?', validation: (val) => val.length > 1 },
    { key: 'requirement', question: 'What specific AI initiatives or challenges are you looking to address?', validation: (val) => val.length > 10 },
    { key: 'timeline', question: 'What\'s your anticipated timeline for implementation?', validation: (val) => val.length > 1 },
    { key: 'budget', question: 'Do you have a preliminary budget range in mind for this initiative?', validation: (val) => val.length > 1 },
    { key: 'details', question: 'Is there anything else about your requirements or objectives that would be helpful for our specialist to know?', validation: (val) => val.length > 5 }
  ];

  // Job application steps
  const jobSteps = [
    { key: 'name', question: 'What\'s your full name?', validation: (val) => val.length > 1 },
    { key: 'email', question: 'What\'s your email address?', validation: (val) => /\S+@\S+\.\S+/.test(val) },
    { key: 'phone', question: 'What\'s your phone number?', validation: (val) => val.length > 5 },
    { key: 'role', question: 'Which position are you interested in?', validation: (val) => val.length > 1 },
    { key: 'city', question: 'Which city are you located in?', validation: (val) => val.length > 1 },
    { key: 'country', question: 'Which country are you from?', validation: (val) => val.length > 1 },
    { key: 'experience', question: 'Tell me about your relevant experience.', validation: (val) => val.length > 10 }
  ];

  useEffect(() => {
    initializeApp();
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (systemSettings?.theme) {
      const root = document.documentElement;
      root.style.setProperty('--primary-color', systemSettings.theme.primaryColor);
      root.style.setProperty('--secondary-color', systemSettings.theme.secondaryColor);
    }
  }, [systemSettings]);

  useEffect(() => {
    if (systemSettings?.theme?.darkMode !== undefined) {
      setDarkMode(systemSettings.theme.darkMode);
    }
  }, [systemSettings]);

  // Authentication state listener
  useEffect(() => {
    const unsubscribe = onAuthStateChange(async (authUser) => {
      console.log('🔐 Auth state changed:', authUser ? 'User signed in' : 'User signed out');
      console.log('🔐 Auth user details:', authUser ? {
        uid: authUser.uid,
        email: authUser.email,
        displayName: authUser.displayName,
        emailVerified: authUser.emailVerified
      } : null);
      
      setUser(authUser);
      if (authUser) {
        console.log('👤 Loading user data for:', authUser.email);
        try {
          // Use comprehensive user sync function
          console.log('🔧 Ensuring user document exists in Firestore...');
          const syncResult = await ensureUserDocumentExists();
          console.log('📝 User sync result:', syncResult);
          
          if (syncResult.success) {
            const userData = await getCurrentUserData();
            console.log('📊 User data loaded:', userData);
            setUserData(userData);
            
            if (userData) {
              console.log('✅ User successfully loaded and available for admin panel');
              toast.success('Profile loaded successfully');
            } else {
              console.log('⚠️ User sync succeeded but data retrieval failed');
              toast.error('Profile sync completed but data retrieval failed. Please refresh the page.');
            }
          } else {
            console.error('❌ User sync failed:', syncResult.error);
            toast.error('Failed to sync user profile: ' + syncResult.error);
            setUserData(null);
          }
        } catch (error) {
          console.error('❌ Error loading user data:', error);
          toast.error('Error loading user profile: ' + error.message);
          setUserData(null);
        }
      } else {
        setUserData(null);
      }
    });

    return () => unsubscribe();
  }, []);

  const initializeApp = async () => {
    const startTime = performance.now();
    
    try {
      setIsLoading(true);
      
      // Log app initialization
      await logSystemEvent('app_initialization_started', 'ChatApp initialization started', 'ChatApp.js');
      
      // Test Firebase connection
      const connected = await testFirebaseConnection();
      setFirebaseConnected(connected);
      
      if (!connected) {
        await logErrorEvent(new Error('Firebase connection failed'), 'ChatApp.js:initializeApp');
        toast.error('Failed to connect to database');
        setIsLoading(false);
        return;
      }

      await logSystemEvent('firebase_connected', 'Firebase connection established successfully', 'ChatApp.js');

      // Initialize default content
      await initializeDefaultContent();
      await logSystemEvent('default_content_initialized', 'Default content initialization completed', 'ChatApp.js');

      // Load content database for RAG
      await loadContentDatabase();

      // Load system settings
      const settings = await getSystemSettings();
      console.log('🎯 DEBUG - System settings loaded:', settings);
      console.log('🎯 DEBUG - Welcome message from settings:', settings?.aiAssistant?.welcomeMessage);
      
      if (settings) {
        setSystemSettings(settings);
        setDarkMode(settings.theme?.darkMode || false);
        
        await logSystemEvent('system_settings_loaded', 'System settings loaded successfully', 'ChatApp.js', { settingsCount: Object.keys(settings).length });
        
        // Detect available AI provider
        const provider = aiService.getEnabledProvider(settings);
        setCurrentProvider(provider);
        
        if (provider) {
          await logSystemEvent('ai_provider_connected', `${provider.type.toUpperCase()} AI provider connected`, 'ChatApp.js', { provider: provider.type });
          toast.success(`🤖 ${provider.type.toUpperCase()} AI connected successfully!`);
        } else {
          await logSystemEvent('ai_provider_fallback', 'No AI provider configured, using fallback responses', 'ChatApp.js');
          toast.warning('⚠️ No AI provider configured - using fallback responses');
        }
      }

      // Test Ollama connection for backward compatibility
      try {
        const ollamaStatus = await ollamaService.isAvailable();
        setOllamaAvailable(ollamaStatus);
        
        if (ollamaStatus) {
          await logSystemEvent('ollama_connected', 'Ollama service connected successfully', 'ChatApp.js');
        }
      } catch (error) {
        console.log('Ollama not available');
        setOllamaAvailable(false);
        await logSystemEvent('ollama_unavailable', 'Ollama service not available', 'ChatApp.js');
      }

      // Add welcome message - always use admin-configured message
      const welcomeMessage = settings?.aiAssistant?.welcomeMessage || 
        `Hello, I'm ${settings?.aiAssistant?.name || 'Jeje'}, a Senior AI Advisory Consultant at Scalovate. I help organizations navigate AI adoption and digital transformation strategies. How can I assist you with your AI initiatives today?`;
      
      console.log('🎯 DEBUG - Final welcome message being set:', welcomeMessage);
      console.log('🎯 DEBUG - Settings object:', settings);
      
      setMessages([{
        id: Date.now(),
        type: 'bot',
        text: welcomeMessage,
        timestamp: new Date()
      }]);

      setIsLoading(false);

    } catch (error) {
      console.error('Error initializing app:', error);
      toast.error('Failed to initialize application');
      setFirebaseConnected(false);
      setIsLoading(false);
    }
  };

  const loadContentDatabase = async () => {
    try {
      const contentQuery = query(collection(db, 'content'), orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(contentQuery);
      
      const contentMap = new Map();
      snapshot.docs.forEach(doc => {
        const data = doc.data();
        if (data.active) { // Only load active content
          contentMap.set(doc.id, {
            id: doc.id,
            subject: data.subject || '',
            category: data.category || '',
            content: data.content || '',
            tags: data.tags || [],
            createdAt: data.createdAt
          });
        }
      });
      
      setContentDatabase(Array.from(contentMap.values()));
      console.log(`✅ Loaded ${contentMap.size} active content items for RAG`);
    } catch (error) {
      console.error('Error loading content database:', error);
      setContentDatabase([]);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const streamText = async (text, messageId) => {
    const words = text.split(' ');
    let currentText = '';
    
    for (let i = 0; i < words.length; i++) {
      currentText += (i > 0 ? ' ' : '') + words[i];
      
      setMessages(prev => prev.map(msg => 
        msg.id === messageId 
          ? { ...msg, text: currentText, isStreaming: i < words.length - 1 }
          : msg
      ));
      
      // Scroll to bottom as text appears
      scrollToBottom();
      
      // Delay between words (adjust speed here)
      await new Promise(resolve => setTimeout(resolve, 50));
    }
    
    // Mark streaming as complete
    setMessages(prev => prev.map(msg => 
      msg.id === messageId 
        ? { ...msg, isStreaming: false }
        : msg
    ));
  };

  const handleSendMessage = async (message) => {
    if (!message.trim()) return;

    console.log('📤 Sending message:', message);
    
    // Note: Jeje AI works for ALL users regardless of authentication status
    // Anonymous users get full AI functionality, authenticated users get personalization

    // Add user message immediately
    const userMessage = {
      id: Date.now(),
      type: 'user',
      text: message,
      timestamp: new Date()
    };

    // Log user message
    await logChatEvent('message_sent', `User sent message: "${message.substring(0, 100)}${message.length > 100 ? '...' : ''}"`, userMessage.id, 1);

    setMessages(prev => [...prev, userMessage]);
    setIsTyping(true);

    try {
      // Update conversation state
      setConversationState(prev => ({
        ...prev,
        messageCount: prev.messageCount + 1
      }));

      // Log conversation progress
      await logChatEvent('conversation_progress', `Conversation message count: ${conversationState.messageCount + 1}`, userMessage.id);

      // Check if we're in a data collection flow
      if (conversationState.collectingLeadInfo) {
        await handleLeadCollection(message);
        return;
      }

      if (conversationState.collectingJobInfo) {
        await handleJobApplicationCollection(message);
        return;
      }

      // Check for lead generation triggers
      const leadTriggers = ['contact', 'consultation', 'demo', 'meeting', 'discuss', 'talk', 'speak', 'call'];
      const isLeadTrigger = leadTriggers.some(trigger => 
        message.toLowerCase().includes(trigger)
      );

      // Check for job application triggers
      const isJobTrigger = isJobApplicationMessage(message);

      let aiResponse = '';

      // Generate AI response with context
      // Check if this is the first user message - AI works for all users regardless of auth status
      const isFirstMessage = conversationState.messageCount === 1;
      const userName = user?.displayName || userData?.name;
      const userDesignation = userData?.designation;
      
      if (currentProvider) {
        try {
          const context = getRelevantContext(message);
          console.log('🧠 Using AI service with context:', context.length, 'items');
          console.log('🧠 Provider details:', currentProvider);
          
          // Build personalization object with user data (if available)
          const personalization = {};
          if (isFirstMessage) {
            personalization.isFirstMessage = true;
            if (userName) {
              personalization.userName = userName;
            }
          }
          if (userDesignation) {
            personalization.userDesignation = userDesignation;
          }
          
          console.log('🎯 DEBUG - Personalization object:', personalization);
          console.log('🎯 DEBUG - Is first message:', isFirstMessage);
          console.log('🎯 DEBUG - User name:', userName);
          console.log('🎯 DEBUG - User designation:', userDesignation);
          console.log('🎯 DEBUG - Will pass to AI service:', Object.keys(personalization).length > 0 ? personalization : null);
          
          // Always generate AI response, with or without personalization
          const response = await aiService.generateResponse(
            message, 
            context, 
            systemSettings, 
            currentProvider,
            (isFirstMessage || Object.keys(personalization).length > 0) ? personalization : null
          );
          console.log('✅ AI service response received:', response?.substring(0, 100));
          aiResponse = response;
        } catch (error) {
          console.error('❌ AI service error:', error);
          aiResponse = generateFallbackResponse(message, getRelevantContext(message), userName);
        }
      } else if (ollamaAvailable) {
        try {
          const context = await ollamaService.retrieveContext(message, contentDatabase);
          console.log('🦙 Using Ollama with context:', context.length, 'items');
          
          const response = await ollamaService.generateResponse(message, context, systemSettings);
          console.log('✅ Ollama response received:', response?.substring(0, 100));
          aiResponse = response;
          
          // Add personalized greeting for first message if user name is available
          if (isFirstMessage && userName) {
            aiResponse = `Hello ${userName}! ${aiResponse}`;
          }
        } catch (error) {
          console.error('❌ Ollama error:', error);
          aiResponse = generateFallbackResponse(message, getRelevantContext(message), userName);
        }
      } else {
        console.log('⚠️ No AI provider available, using fallback');
        aiResponse = generateFallbackResponse(message, getRelevantContext(message), userName);
      }

      // Create bot response with streaming
      const botMessageId = Date.now() + 1;
      const botMessage = {
        id: botMessageId,
        type: 'bot',
        text: '',
        timestamp: new Date(),
        isStreaming: true
      };

      // Add empty bot message first
      setMessages(prev => [...prev, botMessage]);

      // Stream the response text
      await streamText(aiResponse, botMessageId);

      // Handle lead generation flow
      if (isLeadTrigger && !conversationState.leadGenerationTriggered) {
        setTimeout(() => {
          startLeadGenerationFlow();
        }, 1000);
      }

      // Handle job application flow
      if (isJobTrigger && !conversationState.jobApplicationTriggered) {
        setTimeout(() => {
          startJobApplicationFlow();
        }, 1000);
      }

      // Save to Firebase with final response text
      if (firebaseConnected) {
        const finalBotMessage = { ...botMessage, text: aiResponse };
        await saveChatToFirebase(userMessage, finalBotMessage);
      }

      // Text-to-speech if enabled
      if (voiceEnabled) {
        speakText(aiResponse);
      }

    } catch (error) {
      console.error('Error handling message:', error);
      
      const errorMessage = {
        id: Date.now() + 1,
        type: 'bot',
        text: 'I apologize, but I encountered an error processing your message. Please try again.',
        timestamp: new Date()
      };
      
      setMessages(prev => [...prev, errorMessage]);
      toast.error('Failed to process message');
    } finally {
      setIsTyping(false);
    }
  };

  const handleLeadCollection = async (message) => {
    const currentStep = conversationState.currentStep;
    const currentStepData = leadSteps[currentStep];
    
    if (!currentStepData.validation(message)) {
      const errorMessage = {
        id: Date.now(),
        type: 'bot',
        text: `Please provide a valid ${currentStepData.key}. ${currentStepData.question}`,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
      return;
    }

    // Store the response
    const updatedLeadData = {
      ...conversationState.leadData,
      [currentStepData.key]: message
    };

    setConversationState(prev => ({
      ...prev,
      leadData: updatedLeadData
    }));

    // Check if we have more steps
    if (currentStep < leadSteps.length - 1) {
      const nextStep = currentStep + 1;
      const nextQuestion = leadSteps[nextStep].question;
      
      setConversationState(prev => ({
        ...prev,
        currentStep: nextStep
      }));

      const botMessage = {
        id: Date.now(),
        type: 'bot',
        text: nextQuestion,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, botMessage]);
    } else {
      // All steps completed
      await confirmAndSaveLead(updatedLeadData);
    }
  };

  const handleJobApplicationCollection = async (message) => {
    const currentStep = conversationState.currentStep;
    const currentStepData = jobSteps[currentStep];
    
    if (!currentStepData.validation(message)) {
      const errorMessage = {
        id: Date.now(),
        type: 'bot',
        text: `Please provide a valid ${currentStepData.key}. ${currentStepData.question}`,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
      return;
    }

    // Store the response
    const updatedJobData = {
      ...conversationState.jobData,
      [currentStepData.key]: message
    };

    setConversationState(prev => ({
      ...prev,
      jobData: updatedJobData
    }));

    // Check if we have more steps
    if (currentStep < jobSteps.length - 1) {
      const nextStep = currentStep + 1;
      const nextQuestion = jobSteps[nextStep].question;
      
      setConversationState(prev => ({
        ...prev,
        currentStep: nextStep
      }));

      const botMessage = {
        id: Date.now(),
        type: 'bot',
        text: nextQuestion,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, botMessage]);
    } else {
      // All steps completed
      await confirmAndSaveJobApplication(updatedJobData);
    }
  };

  const startLeadGenerationFlow = async () => {
    setConversationState(prev => ({
      ...prev,
      leadGenerationTriggered: true,
      collectingLeadInfo: true,
      currentStep: 0,
      leadData: {}
    }));

    const firstQuestion = leadSteps[0].question;
    const botMessage = {
      id: Date.now(),
      type: 'bot',
      text: `I'd be happy to connect you with our specialists. Let me gather some information first. ${firstQuestion}`,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, botMessage]);
  };

  const startJobApplicationFlow = async () => {
    setConversationState(prev => ({
      ...prev,
      jobApplicationTriggered: true,
      collectingJobInfo: true,
      currentStep: 0,
      jobData: {}
    }));

    const firstQuestion = jobSteps[0].question;
    const botMessage = {
      id: Date.now(),
      type: 'bot',
      text: `I'd be happy to help you with your job application. Let me gather some information first. ${firstQuestion}`,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, botMessage]);
  };

  const confirmAndSaveLead = async (leadData) => {
    try {
      if (firebaseConnected) {
        await addDoc(collection(db, 'leads'), {
          ...leadData,
          timestamp: serverTimestamp(),
          source: 'chat',
          status: 'new'
        });
      }

      setConversationState(prev => ({
        ...prev,
        collectingLeadInfo: false,
        currentStep: null
      }));

      const confirmationMessage = {
        id: Date.now(),
        type: 'bot',
        text: 'Thank you! I\'ve forwarded your information to our specialists. Someone from our team will contact you within 24 hours to discuss your AI initiatives in detail.',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, confirmationMessage]);
    } catch (error) {
      console.error('Error saving lead:', error);
      const errorMessage = {
        id: Date.now(),
        type: 'bot',
        text: 'I apologize, but there was an error saving your information. Please try again or contact us directly.',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    }
  };

  const confirmAndSaveJobApplication = async (jobData) => {
    try {
      if (firebaseConnected) {
        await addDoc(collection(db, 'jobApplications'), {
          ...jobData,
          timestamp: serverTimestamp(),
          source: 'chat',
          status: 'new'
        });
      }

      setConversationState(prev => ({
        ...prev,
        collectingJobInfo: false,
        currentStep: null
      }));

      const confirmationMessage = {
        id: Date.now(),
        type: 'bot',
        text: 'Thank you for your application! I\'ve forwarded your information to our HR team. Someone will contact you within 48 hours regarding the next steps.',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, confirmationMessage]);
    } catch (error) {
      console.error('Error saving job application:', error);
      const errorMessage = {
        id: Date.now(),
        type: 'bot',
        text: 'I apologize, but there was an error saving your application. Please try again or contact us directly.',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    }
  };

  const saveChatToFirebase = async (userMessage, aiMessage) => {
    try {
      if (firebaseConnected) {
        await addDoc(collection(db, 'chats'), {
          userMessage: userMessage.text,
          aiResponse: aiMessage.text, // Changed from aiMessage to aiResponse to match admin panel
          timestamp: serverTimestamp(),
          userId: user?.uid || 'anonymous',
          userName: user?.displayName || 'Anonymous',
          userEmail: user?.email || '',
          isAnonymous: !user?.uid,
          language: 'en'
        });
        console.log('✅ Chat saved to Firebase:', { userMessage: userMessage.text, aiResponse: aiMessage.text });
      }
    } catch (error) {
      console.error('Error saving chat:', error);
    }
  };

  const generateFallbackResponse = (userMessage, context, userName = null) => {
    console.log('🔄 Generating fallback response for:', userMessage);
    console.log('📚 Available context:', context?.length || 0);
    console.log('👤 User name for greeting:', userName);
    
    // Natural, human-like responses
    const lowerMessage = userMessage.toLowerCase();
    const assistantName = systemSettings?.aiAssistant?.name || 'Jeje';
    const greeting = userName ? `Hello ${userName}! ` : '';
    
    if (context && context.length > 0) {
      const relevantInfo = context[0];
      console.log('✅ Using context for fallback:', relevantInfo.subject);
      return `${greeting}That's an excellent question about ${relevantInfo.subject}. Based on my experience working with organizations in this area, I've seen significant benefits when companies implement these strategies effectively. What specific challenges are you facing that we might help address?`;
    }
    
    if (lowerMessage.includes('hello') || lowerMessage.includes('hi') || lowerMessage.includes('hey')) {
      return `${greeting}I'm ${assistantName}, a Senior AI Advisory Consultant at Scalovate. I help organizations navigate AI adoption and digital transformation strategies. How can I assist you with your AI initiatives today?`;
    }
    
    if (lowerMessage.includes('ai') || lowerMessage.includes('artificial intelligence') || lowerMessage.includes('machine learning')) {
      return `That's a fascinating area! AI adoption is indeed a strategic priority for many organizations today. At Scalovate, we specialize in developing practical AI implementation strategies that deliver measurable business value. What industry sector are you operating in?`;
    }
    
    if (lowerMessage.includes('service') || lowerMessage.includes('product') || lowerMessage.includes('solution')) {
      return `Great question! We develop comprehensive AI solutions tailored to specific business requirements - from infrastructure implementation to custom applications. What particular business challenges are you looking to address through technology?`;
    }
    
    if (lowerMessage.includes('contact') || lowerMessage.includes('reach') || lowerMessage.includes('connect')) {
      return `I'd be happy to connect you with our team. You can reach us at support@scalovate.com, or I can gather some information about your requirements first. What's your primary area of interest?`;
    }
    
    if (lowerMessage.includes('cost') || lowerMessage.includes('price') || lowerMessage.includes('budget')) {
      return `Investment in AI solutions varies significantly based on scope and requirements. We typically conduct a thorough assessment to provide accurate projections. What scale of implementation are you considering?`;
    }
    
    if (lowerMessage.includes('time') || lowerMessage.includes('timeline') || lowerMessage.includes('implementation')) {
      return `Implementation timelines depend on project complexity and organizational readiness. Most of our clients see initial results within 3-6 months. What's your target timeframe for deployment?`;
    }
    
    if (lowerMessage.includes('scalovate') || lowerMessage.includes('company') || lowerMessage.includes('about')) {
      return `Scalovate is a leading AI solutions provider specializing in custom AI applications, infrastructure implementation, and strategic consulting. We help organizations transform their operations through intelligent automation and data-driven insights. What specific area would you like to know more about?`;
    }
    
    // Natural response for any topic
    console.log('🎯 Using generic fallback response');
    return `That's an interesting question! I work with many organizations exploring similar concepts. At Scalovate, we often help companies leverage AI to address these types of challenges. Could you tell me more about your specific industry or use case?`;
  };

  const getRelevantContext = (userMessage) => {
    console.log('🔍 RAG Search - User message:', userMessage);
    console.log('📚 Content database size:', contentDatabase?.length || 0);
    
    if (!contentDatabase || contentDatabase.length === 0) {
      console.log('⚠️ No content database available for RAG');
      return [];
    }
    
    const lowerMessage = userMessage.toLowerCase();
    const relevantContent = [];
    
    contentDatabase.forEach((content) => {
      const score = calculateRelevanceScore(lowerMessage, content);
      if (score > 0) {
        relevantContent.push({ ...content, score });
        console.log(`✅ Found relevant content: "${content.subject}" (score: ${score})`);
      }
    });
    
    const sortedContent = relevantContent
      .sort((a, b) => b.score - a.score)
      .slice(0, 3); // Return top 3 most relevant
    
    console.log(`📋 RAG Context: Found ${sortedContent.length} relevant items:`, 
      sortedContent.map(item => ({ subject: item.subject, score: item.score }))
    );
    
    return sortedContent;
  };

  const calculateRelevanceScore = (userMessage, content) => {
    let score = 0;
    const searchTerms = userMessage.split(' ').filter(term => term.length > 2);
    
    // Convert content fields to lowercase for comparison
    const subject = content.subject.toLowerCase();
    const keywords = content.tags.map(tag => tag.toLowerCase()).join(' ');
    const details = content.content.toLowerCase();
    const category = content.category.toLowerCase();
    
    searchTerms.forEach(term => {
      const lowerTerm = term.toLowerCase();
      
      // Exact matches get higher scores
      if (subject.includes(lowerTerm)) score += 5;
      if (keywords.includes(lowerTerm)) score += 4;
      if (category.includes(lowerTerm)) score += 3;
      if (details.includes(lowerTerm)) score += 2;
      
      // Partial matches for compound terms
      if (lowerTerm.length > 4) {
        if (subject.includes(lowerTerm.substring(0, 4))) score += 2;
        if (keywords.includes(lowerTerm.substring(0, 4))) score += 1;
      }
    });
    
    // Boost score for AI-related terms
    const aiTerms = ['ai', 'artificial', 'intelligence', 'machine', 'learning', 'automation', 'llm', 'model', 'rag', 'chatbot', 'assistant'];
    aiTerms.forEach(aiTerm => {
      if (userMessage.toLowerCase().includes(aiTerm)) {
        if (subject.includes(aiTerm) || keywords.includes(aiTerm) || details.includes(aiTerm)) {
          score += 3;
        }
      }
    });
    
    return score;
  };

  const speakText = (text) => {
    if ('speechSynthesis' in window) {
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.voice = speechSynthesis.getVoices().find(voice => 
        voice.name.includes('Female') || voice.name.includes('female')
      ) || speechSynthesis.getVoices()[0];
      utterance.rate = 0.9;
      utterance.pitch = 1.1;
      speechSynthesis.speak(utterance);
    }
  };

  const startListening = () => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = false;
      recognitionRef.current.lang = 'en-US';

      recognitionRef.current.onstart = () => {
        setIsListening(true);
      };

      recognitionRef.current.onresult = (event) => {
        const transcript = event.results[0][0].transcript;
        handleSendMessage(transcript);
      };

      recognitionRef.current.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        setIsListening(false);
        toast.error('Voice recognition failed');
      };

      recognitionRef.current.onend = () => {
        setIsListening(false);
      };

      recognitionRef.current.start();
    } else {
      toast.error('Voice recognition not supported');
    }
  };

  const stopListening = () => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
    }
    setIsListening(false);
  };

  const handleActionButtonClick = (buttonText, buttonKey) => {
    let message = '';
    
    switch (buttonKey) {
      case 'ai-solutions':
        message = 'Tell me about your AI solutions and services';
        break;
      case 'products':
        message = 'What products do you offer?';
        break;
      case 'about':
        message = 'Tell me about Scalovate company';
        break;
      case 'private-ai':
        message = 'What is PrivateAI and how does it work?';
        break;
      case 'ai-usecases':
        message = 'What are some AI use cases you can help with?';
        break;
      case 'contact':
        message = 'How can I contact Scalovate or get in touch with your team?';
        break;
      default:
        message = buttonText;
    }
    
    handleSendMessage(message);
  };

  const toggleDarkMode = () => {
    const newDarkMode = !darkMode;
    setDarkMode(newDarkMode);
    
    // Update system settings if available
    if (systemSettings) {
      const updatedSettings = {
        ...systemSettings,
        theme: { ...systemSettings.theme, darkMode: newDarkMode }
      };
      setSystemSettings(updatedSettings);
      
      // Save to Firebase
      updateSystemSettings(updatedSettings).catch(error => {
        console.error('Failed to save dark mode setting:', error);
      });
    }
  };

  // Add a function to reload system settings
  const reloadSystemSettings = async () => {
    try {
      const settings = await getSystemSettings();
      if (settings) {
        setSystemSettings(settings);
        setDarkMode(settings.theme.darkMode);
        console.log('✅ System settings reloaded');
      }
    } catch (error) {
      console.error('Error reloading system settings:', error);
    }
  };

  // Add periodic check for settings updates (every 30 seconds)
  useEffect(() => {
    if (!firebaseConnected) return;
    
    const interval = setInterval(reloadSystemSettings, 30000);
    return () => clearInterval(interval);
  }, [firebaseConnected]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Initializing {systemSettings?.aiAssistant?.name || 'Jeje'} AI...</p>
          <p className="text-sm text-gray-500 mt-2">Loading knowledge base and connecting to Ollama...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col">
      <Header 
        systemSettings={systemSettings} 
        darkMode={darkMode} 
        user={user || userData}
        onAuthClick={() => setShowAuthModal(true)}
        onProfileClick={() => setShowProfileModal(true)}
        onDarkModeToggle={toggleDarkMode}
      />
      
      <div 
        className="flex-1 flex flex-col p-2 sm:p-4 overflow-hidden"
        style={{
          background: darkMode 
            ? 'linear-gradient(135deg, rgba(17, 24, 39, 0.5) 0%, rgba(31, 41, 55, 0.5) 100%)'
            : 'linear-gradient(135deg, rgba(255, 255, 255, 0.5) 0%, rgba(249, 250, 251, 0.5) 100%)',
          backdropFilter: 'blur(10px)'
        }}
      >
        <div className={`flex-1 flex flex-col w-full max-w-7xl mx-auto rounded-lg sm:rounded-2xl shadow-2xl overflow-hidden ${
          darkMode ? 'bg-gray-900/90' : 'bg-white/50'
        } backdrop-blur-md border ${
          darkMode ? 'border-gray-700/50' : 'border-gray-200/50'
        }`}>
          <ChatHeader
            isVoiceEnabled={voiceEnabled}
            onVoiceToggle={setVoiceEnabled}
            onVoiceStart={startListening}
            firebaseConnected={firebaseConnected}
            ollamaAvailable={ollamaAvailable}
            systemSettings={systemSettings}
            darkMode={darkMode}
          />

          <div className="flex-1 flex flex-col overflow-hidden">
            <div className="flex-1 overflow-hidden">
              <MessageList
                messages={messages}
                isTyping={isTyping}
                darkMode={darkMode}
                systemSettings={systemSettings}
                messagesEndRef={messagesEndRef}
              />
            </div>

            {messages.length === 0 && (
              <div className="flex-shrink-0">
                <ActionButtons
                  onButtonClick={handleActionButtonClick}
                  systemSettings={systemSettings}
                  darkMode={darkMode}
                />
              </div>
            )}

            <div className="flex-shrink-0">
              <ChatInput
                onSendMessage={handleSendMessage}
                disabled={isTyping}
                darkMode={darkMode}
                systemSettings={systemSettings}
                voiceEnabled={voiceEnabled}
                isListening={isListening}
                startListening={startListening}
                stopListening={stopListening}
              />
            </div>
          </div>
        </div>
      </div>

      <Footer systemSettings={systemSettings} darkMode={darkMode} />

      <AnimatePresence>
        {showVoiceModal && (
          <VoiceModal 
            onClose={() => setShowVoiceModal(false)}
            darkMode={darkMode}
          />
        )}
      </AnimatePresence>

      {/* Authentication Modals */}
      <UserAuth
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        darkMode={darkMode}
      />

      <UserArea
        isOpen={showProfileModal}
        onClose={() => setShowProfileModal(false)}
        user={user}
        darkMode={darkMode}
      />
    </div>
  );
};

export default ChatApp; 